/**
 * @fileoverview Notifications Hook
 * 
 * Comprehensive notification management with real-time updates, bulk operations,
 * snooze functionality, and desktop notification integration.
 * 
 * @module hooks/useNotifications
 * @version 1.0.0
 * @since 2024-01-01
 * 
 * @architecture
 * ```
 * useNotifications (this hook)
 *     │
 *     ├── notifications table   ← CRUD operations
 *     ├── Real-time subscription ← Live updates
 *     ├── Desktop notifications ← Browser notifications
 *     └── Sound effects        ← Audio feedback
 * ```
 * 
 * @features
 * - Real-time notification delivery via Supabase subscription
 * - Bulk operations (mark read, archive, delete, snooze)
 * - Desktop notification support with permission handling
 * - Sound effects for different notification types
 * - ATS notification filtering based on feature flag
 * - Snooze with configurable duration
 * - Event tracking for analytics
 * 
 * @example
 * // Basic notification display
 * function NotificationBell() {
 *   const { notifications, unreadCount, markAsRead } = useNotifications();
 *   
 *   return (
 *     <Popover>
 *       <Badge count={unreadCount}>
 *         <BellIcon />
 *       </Badge>
 *       <NotificationList 
 *         items={notifications}
 *         onRead={(id) => markAsRead(id)}
 *       />
 *     </Popover>
 *   );
 * }
 * 
 * @example
 * // Bulk operations
 * const { bulkMarkAsRead, bulkArchive, bulkSnooze } = useNotifications();
 * 
 * // Mark multiple as read
 * bulkMarkAsRead(['id1', 'id2', 'id3']);
 * 
 * // Snooze for 30 minutes
 * bulkSnooze({ notificationIds: ['id1'], durationMinutes: 30 });
 * 
 * @see {@link useNotificationPreferences} - User notification settings
 * @see {@link playNotificationSoundSafe} - Sound utility
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useEffect, useMemo } from "react";
import { playNotificationSoundSafe } from "@/lib/notificationSounds";
import { usePlatformSetting } from "./usePlatformSettings";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/**
 * ATS-related notification types that should be hidden when ATS is disabled.
 * These are filtered out client-side based on the feature_ats_enabled flag.
 * @internal
 */
const ATS_NOTIFICATION_TYPES = [
  'interview_scheduled',
  'application_status',
  'application_received',
  'candidate_applied'
];

import {
  showConnectionRequestNotification,
  showConnectionAcceptedNotification,
  canShowDesktopNotification,
} from "@/lib/desktopNotifications";

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Comprehensive notification management hook.
 * 
 * Provides all notification CRUD operations, real-time updates,
 * bulk actions, and integrates with desktop notifications and sounds.
 * 
 * @returns {Object} Notification state and operations
 * @returns {Array} returns.notifications - Filtered notification list
 * @returns {number} returns.unreadCount - Count of unread notifications
 * @returns {boolean} returns.isLoading - Loading state
 * @returns {Function} returns.markAsRead - Mark single notification as read
 * @returns {Function} returns.markAllAsRead - Mark all notifications as read
 * @returns {Function} returns.deleteNotification - Delete a notification
 * @returns {Function} returns.archiveNotification - Archive a notification
 * @returns {Function} returns.bulkMarkAsRead - Mark multiple as read
 * @returns {Function} returns.bulkArchive - Archive multiple
 * @returns {Function} returns.bulkDelete - Delete multiple
 * @returns {Function} returns.snoozeNotification - Snooze with duration
 * @returns {Function} returns.bulkSnooze - Snooze multiple
 * @returns {Function} returns.trackEvent - Track notification interaction
 * 
 * @caching
 * - staleTime: 2 minutes
 * - gcTime: 5 minutes
 * - refetchOnWindowFocus: false (real-time handles updates)
 * 
 * @realtime
 * Subscribes to INSERT events on notifications table filtered by user_id.
 * Automatically plays sounds and shows desktop notifications for new items.
 */
export function useNotifications() {
  const queryClient = useQueryClient();
  
  // Check if ATS features are enabled
  const { data: atsSetting } = usePlatformSetting('feature_ats_enabled');
  const atsEnabled = atsSetting?.setting_value === true || atsSetting?.setting_value === 'true';

  // ─────────────────────────────────────────────────────────────────
  // Fetch Notifications
  // ─────────────────────────────────────────────────────────────────
  
  /**
   * Fetches notifications excluding snoozed and archived items.
   * Limited to 50 most recent notifications.
   */
  const { data: rawNotifications, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const now = new Date().toISOString();

      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .is("archived_at", null)
        .or(`snoozed_until.is.null,snoozed_until.lt.${now}`)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
    staleTime: 2 * 60 * 1000,      // 2 minutes
    gcTime: 5 * 60 * 1000,          // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  /**
   * Filter out ATS notifications when ATS feature is disabled.
   * This provides client-side filtering based on feature flags.
   */
  const notifications = useMemo(() => {
    if (!rawNotifications) return [];
    if (atsEnabled) return rawNotifications;
    return rawNotifications.filter(n => !ATS_NOTIFICATION_TYPES.includes(n.type));
  }, [rawNotifications, atsEnabled]);

  /** Count of unread notifications */
  const unreadCount = notifications?.filter(n => !n.read_at).length || 0;

  // ─────────────────────────────────────────────────────────────────
  // Single Item Mutations
  // ─────────────────────────────────────────────────────────────────

  /** Mark single notification as read */
  const markAsRead = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase.rpc("mark_notification_read", {
        p_notification_id: notificationId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  /** Mark all notifications as read */
  const markAllAsRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("mark_all_notifications_read");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast({
        title: "All notifications marked as read",
      });
    },
  });

  /** Delete a single notification */
  const deleteNotification = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .delete()
        .eq("id", notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  /** Archive a single notification */
  const archiveNotification = useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ archived_at: new Date().toISOString() })
        .eq("id", notificationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  // ─────────────────────────────────────────────────────────────────
  // Bulk Mutations
  // ─────────────────────────────────────────────────────────────────

  /** Mark multiple notifications as read */
  const bulkMarkAsRead = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const { data, error } = await supabase.rpc("bulk_mark_notifications_read", {
        p_notification_ids: notificationIds,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast({
        title: "Notifications marked as read",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to mark notifications as read",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  /** Archive multiple notifications */
  const bulkArchive = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const { data, error } = await supabase.rpc("bulk_archive_notifications", {
        p_notification_ids: notificationIds,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast({
        title: "Notifications archived",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to archive notifications",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  /** Delete multiple notifications */
  const bulkDelete = useMutation({
    mutationFn: async (notificationIds: string[]) => {
      const { data, error } = await supabase.rpc("bulk_delete_notifications", {
        p_notification_ids: notificationIds,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast({
        title: "Notifications deleted",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete notifications",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // ─────────────────────────────────────────────────────────────────
  // Snooze Mutations
  // ─────────────────────────────────────────────────────────────────

  /**
   * Snooze a notification for a specified duration.
   * The notification will reappear after the snooze period.
   */
  const snoozeNotification = useMutation({
    mutationFn: async ({ notificationId, durationMinutes }: { 
      notificationId: string; 
      durationMinutes: number;
    }) => {
      const { error } = await supabase.rpc("snooze_notification", {
        p_notification_id: notificationId,
        p_snooze_duration_minutes: durationMinutes,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast({
        title: "Notification snoozed",
        description: "You'll be reminded later",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to snooze notification",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  /** Snooze multiple notifications */
  const bulkSnooze = useMutation({
    mutationFn: async ({ notificationIds, durationMinutes }: { 
      notificationIds: string[]; 
      durationMinutes: number;
    }) => {
      await Promise.all(
        notificationIds.map(id =>
          supabase.rpc("snooze_notification", {
            p_notification_id: id,
            p_snooze_duration_minutes: durationMinutes,
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      toast({
        title: "Notifications snoozed",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to snooze notifications",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // ─────────────────────────────────────────────────────────────────
  // Event Tracking
  // ─────────────────────────────────────────────────────────────────

  /**
   * Track notification interaction events for analytics.
   * Events include: viewed, clicked, dismissed, etc.
   */
  const trackEvent = useMutation({
    mutationFn: async ({
      notificationId,
      eventType,
      deviceType = 'web',
      metadata = {},
    }: {
      notificationId: string;
      eventType: string;
      deviceType?: string;
      metadata?: any;
    }) => {
      const { error } = await supabase.rpc("track_notification_event", {
        p_notification_id: notificationId,
        p_event_type: eventType,
        p_device_type: deviceType,
        p_metadata: metadata,
      });
      if (error) throw error;
    },
  });

  // ─────────────────────────────────────────────────────────────────
  // Real-Time Subscription
  // ─────────────────────────────────────────────────────────────────

  /**
   * Subscribe to real-time notification inserts.
   * Handles sound effects and desktop notifications for new items.
   */
  useEffect(() => {
    let channel: any;

    const setupSubscription = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log("Setting up real-time notifications subscription for user:", user.id);

      channel = supabase
        .channel('notifications-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          async (payload) => {
            console.log("Real-time notification received:", payload);
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            
            const notification = payload.new as any;
            
            // Play sound based on notification type
            if (notification.type === 'connection_request_received') {
              playNotificationSoundSafe('connection_request');
            } else if (notification.type === 'connection_request_accepted') {
              playNotificationSoundSafe('connection_accepted');
            } else if (notification.priority === 'critical' || notification.priority === 'high') {
              playNotificationSoundSafe('default');
            }
            
            // Show desktop notification if tab is not visible
            if (canShowDesktopNotification()) {
              if (notification.type === 'connection_request_received') {
                showConnectionRequestNotification(
                  notification.title,
                  notification.related_user_id || undefined
                );
              } else if (notification.type === 'connection_request_accepted') {
                showConnectionAcceptedNotification(
                  notification.title,
                  notification.related_user_id || undefined
                );
              }
            }
            
            // Show toast for new notification
            toast({
              title: notification.title,
              description: notification.message,
            });
          }
        )
        .subscribe();
    };

    setupSubscription();

    return () => {
      if (channel) {
        console.log("Cleaning up notifications subscription");
        supabase.removeChannel(channel);
      }
    };
  }, [queryClient]);

  // ─────────────────────────────────────────────────────────────────
  // Return Value
  // ─────────────────────────────────────────────────────────────────

  return {
    notifications: notifications || [],
    unreadCount,
    isLoading,
    markAsRead: markAsRead.mutate,
    markAllAsRead: markAllAsRead.mutate,
    deleteNotification: deleteNotification.mutate,
    archiveNotification: archiveNotification.mutate,
    bulkMarkAsRead: bulkMarkAsRead.mutate,
    bulkArchive: bulkArchive.mutate,
    bulkDelete: bulkDelete.mutate,
    snoozeNotification: snoozeNotification.mutate,
    bulkSnooze: bulkSnooze.mutate,
    trackEvent: trackEvent.mutate,
  };
}
